import { message } from 'antd';
import { AGENT_PROCEDURES_CRUD_ACTION_CREATORS } from '../actions/agent_procedures_actions';
import FetchUtils from './fetch_utils';
import { AOP_JSON_HEADERS, agentRequest } from './agent_service_request';
import { getAgentIntelligenceUri } from '../utils/EnvConfigUtils';
import { AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS } from '../actions/agent_procedure_versions_actions';
import { AOPDraft, AOPDraftPlan, AOPVersion, AOPVersionSummary } from '../types/AOPTypes';

const fetchUtils = new FetchUtils();

/**
 * Procedure authoring against openwrench-intelligence-service
 * (emptor-ai/openwrench-intelligence-service#20, `/api/v1/buyer/agent`).
 *
 * That service is its own deployment rather than a route on the Play backend,
 * so these calls go to `getAgentIntelligenceUri()`. It authenticates with the
 * buyer app's `X-Auth-Token`, which `FetchUtils` already puts on every request.
 *
 * It is FastAPI, so a success is the bare model and a failure is
 * `{ "error": { "code", "correlation_id" } }` — not this app's usual
 * `{ status, data }` envelope.
 */

const procedureUrl = (procedureId: string, suffix: string): string =>
	`${getAgentIntelligenceUri()}/api/v1/buyer/agent/procedures/${procedureId}${suffix}`;

/** The stored record: a draft keyed by its own procedure id. */
export interface AOPDraftRecord extends AOPDraft {
	id: string;
}

const toRecord = (draft: AOPDraft): AOPDraftRecord => ({ id: draft.procedure.id, ...draft });

/**
 * Reads the draft, replacing whatever the store held for it — the plan
 * included.
 *
 * That matters because the plan in the store is the author's: the canvas
 * writes an edit there before the service is told. A read landing while an
 * edit is outstanding therefore overwrites it, so every caller has to be
 * somewhere the canvas cannot be edited. Today all three are behind a modal —
 * publishing, a refused publish, and restoring a version — and the fourth is
 * the detail page's own load, where nothing is outstanding yet.
 *
 * If a reread is ever added somewhere the canvas is live, it needs to leave
 * the plan alone while a save is outstanding, the way a draft *write* already
 * does.
 */
export const fetchProcedureDraft =
	(procedureId: string) =>
	(dispatch): Promise<AOPDraftRecord> => {
		dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.fetchStart());

		return agentRequest<AOPDraftRecord>(
			dispatch,
			fetchUtils.get(procedureUrl(procedureId, '/draft'), { headers: AOP_JSON_HEADERS }),
			{
				fallback: 'procedure_unavailable',
				onFailure: (code) => dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.fetchError(code, null)),
				onSuccess: (json) => {
					const record = toRecord(json);
					dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.fetchOneSuccess(record));
					return record;
				},
			}
		);
	};

/** What a draft write may carry. Every field is optional on a PATCH. */
export interface AOPDraftPatch {
	prose?: string;
	plan?: AOPDraftPlan;
	max_run_seconds?: number;
	/**
	 * Suggestions the author has ruled on. Additive — an accepted suggestion
	 * stays accepted — so only newly accepted ids need sending, and deleting a
	 * suggested step marks its suggestion removed with nothing sent at all.
	 *
	 * It has a default upstream, so it is only sent when there is something to
	 * rule on. Omitting it entirely is what made Approve do nothing: the
	 * suggestion came back `open` on the next read.
	 */
	accept?: string[];
}

/**
 * Writes a draft, naming only the fields it means to change.
 *
 * `PUT /draft` also exists and is marked deprecated upstream — "kept until
 * clients move to PATCH". Both routes call the same function there and write
 * only the fields that are set, so the two differ in their contract and
 * nothing else: PUT requires prose and plan, PATCH takes any of them. Every
 * call here names what it writes, so there is nothing PUT was doing for us.
 */
const sendDraft =
	(procedureId: string, data: AOPDraftPatch, adoptPlan: boolean) =>
	(dispatch): Promise<AOPDraftRecord> => {
		/*
		  The slice tracks a write only when the write replaces the document.

		  `updating` is cleared by `updateSuccess`, and the partial path cannot
		  dispatch that: it replaces `records[id]` wholesale, so sending it a
		  record with the plan stripped would take the plan out of the store the
		  canvas is drawing from. It merged a delta instead and left `updating`
		  true — so the first autosave raised a flag nothing ever lowered, and the
		  strip showed "Saving…" for the rest of the session.

		  Nothing is lost by not raising it. `useDraftSave` already knows whether a
		  plan write is outstanding, and that is what the strip reads. The flag now
		  means what the one place reading it wants: a whole document on its way.
		*/
		if (adoptPlan) {
			dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.updateStart({ id: procedureId }));
		}

		return agentRequest<AOPDraftRecord>(
			dispatch,
			fetchUtils.patch(procedureUrl(procedureId, '/draft'), {
				headers: AOP_JSON_HEADERS,
				data,
			}),
			{
				fallback: 'draft_not_saved',
				onFailure: (code) => {
					// Paired with the start above: raised and lowered together, or
					// neither. A partial write's failure is the hook's to report, and
					// it does, beside the plan the service would not take.
					if (adoptPlan) {
						dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.updateError(code, { id: procedureId }));
					}

					message.error(
						code === 'plan_too_large'
							? 'This procedure is too large to save.'
							: 'Could not save the procedure.'
					);
				},
				onSuccess: (json) => {
					const record = toRecord(json);

					if (adoptPlan) {
						dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.updateSuccess(record));
					} else {
						/*
						  Everything the service decided, and not the plan.

						  The canvas writes its edit to the store and sends it in
						  parallel, and only the newest is ever sent — so the plan in
						  this response is either the one the store already holds or one
						  an edit has since moved past. Adopting it could only ever put
						  an older plan back. The findings, the suggestions and
						  `publishable` are what this answer is for.
						*/
						const { plan, ...withoutPlan } = record;
						dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.fetchOneDeltaSuccess(withoutPlan));
					}

					return record;
				},
			}
		);
	};

/**
 * Changes only what it names, leaving the rest of the draft as the service
 * holds it.
 *
 * An edit on the canvas changes the plan and nothing else, and ruling on a
 * suggestion changes neither the prose nor the plan. Sending the untouched
 * fields back meant sending a copy read before whatever else had happened to
 * the draft since — so a second edit could undo a first one that had already
 * landed. What is not named here is not written.
 */
export const patchProcedureDraft = (procedureId: string, patch: AOPDraftPatch) =>
	sendDraft(procedureId, patch, false);

/**
 * The whole document, for the two callers that mean it: creating a procedure's
 * first draft from the review screen, and restoring a published version over
 * the draft. Both name every field, so neither is reading a copy it might be
 * about to overwrite somebody else's edit with.
 *
 * `accept` is left out when there is nothing to rule on — it has a default
 * upstream, and an empty list would say "these are the ones I accept" about no
 * suggestions at all.
 */
export const writeWholeProcedureDraft = (
	procedureId: string,
	prose: string,
	plan: AOPDraftPlan,
	accept?: string[],
	/**
	 * The run limit, where the caller has one to give.
	 *
	 * Restoring does: a published version froze its own, and leaving it out sent
	 * a draft that read as that version while running under whatever limit the
	 * current draft happened to carry. Creating does not — there is no earlier
	 * document to take one from, and the service applies its own default.
	 */
	maxRunSeconds?: number
) =>
	sendDraft(
		procedureId,
		{
			prose,
			plan,
			...(accept && accept.length > 0 ? { accept } : {}),
			...(maxRunSeconds === undefined || maxRunSeconds === null
				? {}
				: { max_run_seconds: maxRunSeconds }),
		},
		// This one means "the draft is now exactly this" — a restore, or the first
		// draft cut from the create flow — so its plan is the answer, not an echo.
		true
	);

/**
 * Cuts an immutable numbered version from the draft. The service refuses while
 * any finding is an error, and a draft identical to the latest version
 * publishes nothing and returns that version.
 */
export const publishProcedure =
	(procedureId: string, changeNote: string | null) =>
	(dispatch): Promise<any> =>
		agentRequest<any>(
			dispatch,
			fetchUtils.post(procedureUrl(procedureId, '/publish'), {
				headers: AOP_JSON_HEADERS,
				data: { change_note: changeNote },
			}),
			{
				fallback: 'publish_failed',
				onFailure: (code) => {
					// `draft_not_publishable` is reported by the screen, which can name
					// the findings in the way of it. A message here as well would say
					// the same thing twice, less usefully.
					if (code !== 'draft_not_publishable') {
						message.error('Could not publish this procedure.');
					}
				},
				onSuccess: (json) => {
					message.success(`Published version ${json.published_version}.`);
					return json;
				},
			}
		);

/* -------------------------------------------------------------------------- */
/*                             Published versions                             */
/* -------------------------------------------------------------------------- */

/** One page of the version history. `limit` is capped at 100 upstream. */
export const AOP_VERSIONS_PAGE_SIZE = 50;

/**
 * The first page, which is read hydrated.
 *
 * Short because every row then carries a whole prose and plan rather than a
 * label. It only has to be long enough to usually hold the live version, which
 * is what the screen compares the draft against; anything older is read on its
 * own by the effect that fetches what is missing.
 */
export const AOP_VERSIONS_FIRST_PAGE = 10;

/** A page of the history, and how many there are behind it. */
export interface AOPVersionPage {
	summaries: AOPVersionSummary[];
	count: number;
	offset: number;
}

/**
 * The version history, newest first.
 *
 * Paged explicitly rather than taking whatever the endpoint defaults to: it
 * answers `{ data, count }` now and caps `limit` at 100, so a procedure past
 * the default would quietly lose its oldest versions from the picker.
 *
 * `hydrate` asks for each version whole instead of as a summary. The Procedure
 * tab uses it because it needs the selected and the published documents to
 * draw and to compare, and they are almost always on the first page — the
 * reads for anything missing still happen one at a time, so an older version
 * costs its own call rather than the whole history being pulled.
 */
export const fetchProcedureVersions =
	(procedureId: string, params: { offset?: number; limit?: number; hydrate?: boolean } = {}) =>
	(dispatch): Promise<AOPVersionPage> => {
		const offset = params.offset ? params.offset : 0;
		const limit = params.limit ? params.limit : AOP_VERSIONS_PAGE_SIZE;
		const query = `/versions?offset=${offset}&limit=${limit}${
			params.hydrate ? '&hydrate=true' : ''
		}`;

		dispatch(AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchStart());

		return agentRequest<AOPVersionPage>(
			dispatch,
			fetchUtils.get(procedureUrl(procedureId, query), { headers: AOP_JSON_HEADERS }),
			{
				fallback: 'versions_unavailable',
				onFailure: (code) => dispatch(AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchError(code)),
				onSuccess: (json) => {
					/*
					  `{ data, count }`, not a bare array. Read loudly: the rows were the
					  body itself until the service wrapped them, and a shape we do not
					  expect would otherwise empty the picker without a word.
					*/
					const rows = json ? json.data : null;

					if (!Array.isArray(rows)) {
						throw new Error('versions_unexpected_shape');
					}

					const summaries: AOPVersionSummary[] = rows
						.slice()
						.sort((left, right) => right.version - left.version);
					const count = typeof json.count === 'number' ? json.count : summaries.length;

					dispatch(
						AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchListSuccess(
							procedureId,
							summaries,
							count,
							// A page past the first adds to the history held; the first
							// replaces it.
							offset > 0,
							offset
						)
					);

					/*
					  A hydrated row is a whole version, so it is stored as one. The
					  canvas and the draft comparison read the same place whether a
					  version arrived with the list or was read on its own, and the
					  effect that reads missing versions then has nothing to do.
					*/
					summaries.forEach((summary) => {
						if ((summary as any).plan) {
							dispatch(
								AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchOneSuccess(
									procedureId,
									summary as AOPVersion
								)
							);
						}
					});

					return { summaries, count, offset };
				},
			}
		);
	};

/** One published version, with the prose and plan it froze. */
export const fetchProcedureVersion =
	(procedureId: string, version: number) =>
	(dispatch): Promise<AOPVersion> => {
		dispatch(AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchStart());

		return agentRequest<AOPVersion>(
			dispatch,
			fetchUtils.get(procedureUrl(procedureId, `/versions/${version}`), {
				headers: AOP_JSON_HEADERS,
			}),
			{
				fallback: 'version_unavailable',
				onFailure: (code) => dispatch(AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchError(code)),
				onSuccess: (json) => {
					dispatch(AGENT_PROCEDURE_VERSIONS_ACTION_CREATORS.fetchOneSuccess(procedureId, json));
					return json;
				},
			}
		);
	};

/**
 * Creates the procedure itself. A scenario is created separately and linked to
 * it, because the service keeps routing (which work orders reach Radar) apart
 * from the procedure (what Radar then does).
 */
/**
 * Renames a procedure, or changes its description or time limit.
 *
 * Every field is optional upstream, so this sends only what it is given — a
 * `PATCH` carrying the whole record would rewrite fields nobody touched.
 */
export const updateProcedure =
	(procedureId: string, patch: { name?: string; description?: string | null }) =>
	(dispatch): Promise<any> =>
		agentRequest<any>(
			dispatch,
			fetchUtils.patch(procedureUrl(procedureId, ''), {
				headers: AOP_JSON_HEADERS,
				data: patch,
			}),
			{
				fallback: 'procedure_not_updated',
				// Nothing in the slice changes on a refusal, so there is nothing to
				// stand down; the caller reports it.
				onFailure: () => undefined,
				onSuccess: (json) => json,
			}
		);

export const createProcedure =
	(name: string, description: string | null) =>
	(dispatch): Promise<any> => {
		dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.createStart({ name }));

		return agentRequest<any>(
			dispatch,
			fetchUtils.post(`${getAgentIntelligenceUri()}/api/v1/buyer/agent/procedures`, {
				headers: AOP_JSON_HEADERS,
				// `kind` is a const upstream: dispatch is the only kind that exists.
				data: { kind: 'dispatch', name, description },
			}),
			{
				fallback: 'procedure_not_created',
				onFailure: (code) =>
					dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.createError(code, { name })),
				onSuccess: (json) => {
					dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.createSuccess(json));
					return json;
				},
			}
		);
	};

/**
 * Turns the author's prose into a draft, against a procedure that already
 * exists.
 *
 * It replaces that procedure's draft rather than handing back a plan to hold
 * onto, so the response is the whole draft — plan, suggestions, violations and
 * whether it could be published — and it lands in the store like any read.
 */
export const compileProcedureDraft =
	(procedureId: string, prose: string) =>
	(dispatch): Promise<AOPDraftRecord> =>
		agentRequest<AOPDraftRecord>(
			dispatch,
			fetchUtils.post(procedureUrl(procedureId, '/compile'), {
				headers: AOP_JSON_HEADERS,
				data: { prose },
			}),
			{
				fallback: 'compiler_unavailable',
				onFailure: (code) =>
					message.error(
						code === 'compiler_unavailable'
							? 'Radar could not generate steps just now. Try again in a moment.'
							: 'Could not turn that description into steps.'
					),
				onSuccess: (json) => {
					const record = toRecord(json);
					dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.fetchOneSuccess(record));
					return record;
				},
			}
		);
